/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { FC } from 'react';
import isEmpty from 'lodash/isEmpty';
import { classes, cssRaw, style } from 'typestyle';
import { center, flexRoot } from 'csstips';
import { important, px } from 'csx/lib/internal';
import { colorGreyAccent, colorGunmetal, colorNeutralLight, colorWhite, rebrand } from '../../../theme/color';
import { History as HistoryIcon } from './icons/History';
import { Profile } from './icons/Profile';
import { PlaylistHamburger } from './icons/PlaylistHamburger';
import { TrackIcon } from './icons/TrackIcon';
import { GenreIcon } from './icons/GenreIcon';
import { MoodIcon } from './icons/MoodIcon';
import { NoVocal } from './icons/NoVocalInstrumental';
import { CloseCross } from './icons/CloseCross';
import { Component, ComponentProps, deps, inject, observer } from '../../../lib/component';
import { runInAction } from 'mobx';
import { ArtistSchema, ImageSchema, TrackSchema } from '../../../types/schema';
import { spacing } from '../../../theme/spacing';
import { NonActiveArtist } from './icons/NonActiveArtist';
import { borderRadius } from '../../../theme/border';

const styles = {
  wrapper: style(flexRoot, center, {
    backgroundColor: colorWhite.toHexString(),
    padding: px(12),
    borderTop: `solid 1px ${rebrand.neutralOnDark[20]}`,
    maxHeight: px(48),
    cursor: 'pointer',
    gap: spacing.XSMALL,
    $nest: {
      '> .cancel': {
        display: 'none',
        opacity: 0,
      },
    },
  }),
  active: style({
    backgroundColor: colorGreyAccent.toString(),
    $nest: {
      '> .cancel': {
        display: 'block',
        opacity: 1,
      },
    },
  }),
  imageWrapper: style({
    position: 'relative',
    height: '32px',
    width: '32px',
    borderRadius: borderRadius.SMALL,
    overflow: 'hidden',
  }),
  image: style({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  }),
  entityWrapper: style({
    flex: 1,
  }),
  title: style({
    fontSize: px(16),
    color: colorGunmetal.toString(),
    lineHeight: px(24),
    fontWeight: 400,
    margin: 0,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  }),
  type: style({
    fontSize: px(12),
    lineHeight: px(11),
    fontWeight: 400,
    color: colorNeutralLight.toString(),
    margin: 0,
    textTransform: 'capitalize',
  }),
  subtitle: style({
    fontSize: px(16),
    color: colorNeutralLight.toString(),
    margin: 0,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  }),
  historytitle: style({
    color: colorNeutralLight.toString(),
  }),
  svg: style({
    minWidth: px(24),
    minHeight: px(24),
  }),
};

export type SuggestionType = 'artist' | 'playlist' | 'mood' | 'genre' | 'track' | 'history' | 'instrumental';

const EntityIcon: React.FC<{ type: SuggestionType | 'nonActiveArtist' }> = ({ type }) => {
  const className = styles.svg;

  switch (type) {
    case 'artist':
      return <Profile className={className} />;
    case 'nonActiveArtist':
      return <NonActiveArtist className={className} />;
    case 'playlist':
      return <PlaylistHamburger className={className} />;
    case 'mood':
      return <MoodIcon className={className} />;
    case 'genre':
      return <GenreIcon className={className} />;
    case 'track':
      return <TrackIcon className={className} />;
    case 'instrumental':
      return <NoVocal className={className} />;
    case 'history':
      return <HistoryIcon className={className} />;
    default:
      return null;
  }
};

export type SearchSuggestionType = {
  type: SuggestionType;
  name?: string;
  title?: string;
  uuid?: string;
  position?: number;
  slug?: string;
  context?: string;
  tracks?: TrackSchema[];
  images?: ImageSchema;
  artists?: Array<ArtistSchema>;
  index?: number;
  identity?: string;
  is_available_in_territory?: boolean;
  is_stock_music?: boolean;
  is_newly_activated?: boolean;
  is_non_active_artist?: boolean;
};

interface SearchSuggestionItemProps extends ComponentProps {
  entity: SearchSuggestionType;
  index?: number;
}

@inject(deps)
@observer
class SearchSuggestionItem extends Component<SearchSuggestionItemProps> {
  render() {
    const {
      entity,
      index,
      controller: {
        page: {
          searchMvp: { deleteUserSearchTerm, handleSuggestionSelection, performSearch },
        },
        analytics: { sendMixpanel },
      },
      model: {
        page: { searchMvp },
        image: { cover },
      },
    } = this.props;

    const handleMouseEnter = (e) => {
      this.props.model.page.searchMvp.cursor = index;
    };

    const handleMouseLeave = (e) => {
      this.props.model.page.searchMvp.cursor = null;
    };
    const activeItem = index === searchMvp.cursor;

    const { type, name, artists } = entity;

    const showPreviewImage = ['track', 'artist', 'playlist'].includes(type) && !!entity.images?.identity;
    const trackArtist = type === 'track' ? artists[0].name : null;
    const isNonActiveArtist = type === 'artist' && entity.is_non_active_artist;

    const subtitle = trackArtist
      ? trackArtist
      : type === 'instrumental'
      ? 'Tracks'
      : isNonActiveArtist
      ? 'Artists'
      : type;

    if (type === 'history') {
      return (
        <div
          className={classes(styles.wrapper, activeItem && styles.active)}
          data-test-view-search-suggestion={type}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={() => {
            const saveSuggestion = JSON.parse(entity.context);
            // This will be removed later when we have most user search data updated;
            if (isEmpty(saveSuggestion)) {
              this.props.model.page.searchMvp.searchState.query = name;
              performSearch();
            } else {
              if (saveSuggestion.type === 'track') {
                saveSuggestion.title = saveSuggestion.name;
              }
              handleSuggestionSelection(saveSuggestion);
            }
          }}
        >
          <EntityIcon type="history" />

          <div className={styles.entityWrapper}>
            <p className={classes(styles.title, styles.historytitle)}>{name}</p>
          </div>

          {entity.uuid && (
            <CloseCross
              className={'cancel'}
              title="Remove history"
              size={20}
              color={colorNeutralLight.toString()}
              onClick={(e) => {
                runInAction(() => {
                  e.stopPropagation();
                  deleteUserSearchTerm(entity.uuid);
                });
              }}
            />
          )}
        </div>
      );
    }

    return (
      <div
        className={classes(styles.wrapper, activeItem && styles.active)}
        data-test-view-search-suggestion={type}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => {
          handleSuggestionSelection(entity);
          sendMixpanel('User clicks on a search suggestion', {
            type: isNonActiveArtist ? 'Non-active artist' : type,
            artist: trackArtist || undefined,
            name: name,
            position: index + 1,
          });
        }}
      >
        <EntityIcon type={isNonActiveArtist ? 'nonActiveArtist' : type} />

        {showPreviewImage && (
          <div className={styles.imageWrapper}>
            <img
              src={cover(entity.images.identity, {
                ignoreDimensions: true,
                width: 64,
                height: 64,
                crop: true,
              })}
              alt={name}
              className={styles.image}
            />
          </div>
        )}

        <div className={styles.entityWrapper}>
          <p className={styles.title}>
            {isNonActiveArtist ? (
              <>
                Similar to <strong>{name}</strong>
              </>
            ) : (
              name
            )}
          </p>
          <p className={styles.type}>{subtitle}</p>
        </div>
      </div>
    );
  }
}

export default SearchSuggestionItem;
